import React, { useEffect, useMemo, useState } from "react";
import { Badge, Button, Modal } from "react-bootstrap";
import { toast } from "react-toastify";
import {
  MdCheck,
  MdCheckCircle,
  MdContentCopy,
  MdEmail,
  MdLockOutline,
  MdPhoneIphone,
  MdWarningAmber,
} from "react-icons/md";

const palette = {
  ink: "#132238",
  muted: "#64748b",
  border: "#dbe4ee",
  canvas: "#f6f9fc",
  green: "#087443",
  greenSoft: "#eaf8f0",
  blue: "#0b4aa2",
  blueSoft: "#edf5ff",
  amber: "#9a5b00",
  amberSoft: "#fff8e7",
};

const credentialText = (credentials) => {
  const lines = [
    "Suku Rider App sign-in",
    credentials?.name && `Rider: ${credentials.name}`,
    credentials?.phone && `Phone: ${credentials.phone}`,
    credentials?.email && `Email: ${credentials.email}`,
    credentials?.tempPassword && `Temporary password: ${credentials.tempPassword}`,
    "",
    "Recommended: sign in with the phone number and OTP.",
    credentials?.tempPassword && "Change the temporary password after first sign-in.",
  ];
  return lines.filter((line) => line !== false && line !== undefined && line !== null).join("\n");
};

const RiderCredentialsModal = ({ show, onHide, credentials }) => {
  const [copied, setCopied] = useState(false);
  const shareText = useMemo(() => credentialText(credentials), [credentials]);
  const initials = String(credentials?.name || "Rider")
    .split(/\s+/)
    .filter(Boolean)
    .slice(0, 2)
    .map((part) => part[0])
    .join("")
    .toUpperCase();

  useEffect(() => {
    if (show) setCopied(false);
  }, [show]);

  const copyCredentials = async () => {
    try {
      await navigator.clipboard.writeText(shareText);
      setCopied(true);
      toast.success("Rider sign-in details copied");
    } catch (_error) {
      toast.error("Could not copy. Please select the details manually.");
    }
  };

  return (
    <Modal show={show} onHide={onHide} centered size="lg" backdrop="static">
      <Modal.Header closeButton style={{ borderBottom: `1px solid ${palette.border}`, padding: "22px 26px" }}>
        <div className="d-flex align-items-center gap-3">
          <div
            style={{
              width: 46,
              height: 46,
              borderRadius: 14,
              background: palette.greenSoft,
              color: palette.green,
              display: "grid",
              placeItems: "center",
            }}
          >
            <MdCheckCircle size={27} />
          </div>
          <div>
            <Modal.Title>
              Rider account created
            </Modal.Title>
            <div style={{ color: palette.muted, marginTop: 3, fontSize: "0.9rem" }}>
              Share these one-time sign-in details securely with the rider.
            </div>
          </div>
        </div>
      </Modal.Header>

      <Modal.Body style={{ background: palette.canvas, padding: 26 }}>
        <div
          className="d-flex align-items-center gap-3"
          style={{ background: "#fff", border: `1px solid ${palette.border}`, borderRadius: 16, padding: 18 }}
        >
          <div
            style={{
              width: 52,
              height: 52,
              borderRadius: 16,
              background: "linear-gradient(135deg, #0b4aa2, #087443)",
              color: "#fff",
              display: "grid",
              placeItems: "center",
              fontWeight: 800,
              letterSpacing: "0.04em",
            }}
          >
            {initials || "RD"}
          </div>
          <div style={{ minWidth: 0 }}>
            <div style={{ color: palette.ink, fontWeight: 800, fontSize: "1rem" }}>{credentials?.name}</div>
            <div className="d-flex flex-wrap gap-3" style={{ color: palette.muted, marginTop: 5, fontSize: "0.86rem" }}>
              {credentials?.phone && <span><MdPhoneIphone className="me-1" />{credentials.phone}</span>}
              {credentials?.email && <span><MdEmail className="me-1" />{credentials.email}</span>}
            </div>
          </div>
        </div>

        <div className="row g-3 mt-1">
          <div className="col-md-6">
            <div style={{ height: "100%", background: "#fff", border: `1px solid ${palette.border}`, borderRadius: 16, padding: 18 }}>
              <div className="d-flex justify-content-between align-items-center mb-3">
                <div style={{ color: palette.blue, fontWeight: 800 }}><MdPhoneIphone className="me-2" />Phone and OTP</div>
                <Badge pill style={{ background: palette.blueSoft, color: palette.blue, border: "1px solid #bfdbfe" }}>Recommended</Badge>
              </div>
              <div style={{ color: palette.muted, fontSize: "0.88rem", lineHeight: 1.55 }}>
                Enter <strong style={{ color: palette.ink }}>{credentials?.phone}</strong> in the Suku Rider App, then verify the OTP sent to that number.
              </div>
            </div>
          </div>

          <div className="col-md-6">
            <div style={{ height: "100%", background: "#fff", border: `1px solid ${palette.border}`, borderRadius: 16, padding: 18 }}>
              <div style={{ color: palette.ink, fontWeight: 800, marginBottom: 12 }}><MdLockOutline className="me-2" />Email and password</div>
              {credentials?.tempPassword ? (
                <>
                  <div style={{ color: palette.muted, fontSize: "0.78rem", fontWeight: 700, textTransform: "uppercase", letterSpacing: "0.05em" }}>
                    Temporary password
                  </div>
                  <div
                    style={{
                      marginTop: 7,
                      padding: "11px 13px",
                      borderRadius: 10,
                      background: "#f8fafc",
                      border: `1px dashed ${palette.border}`,
                      color: palette.ink,
                      fontFamily: "ui-monospace, SFMono-Regular, Menlo, monospace",
                      fontWeight: 800,
                      letterSpacing: "0.04em",
                      overflowWrap: "anywhere",
                    }}
                  >
                    {credentials.tempPassword}
                  </div>
                </>
              ) : (
                <div style={{ color: palette.muted, fontSize: "0.88rem" }}>Use the password entered while creating this rider.</div>
              )}
            </div>
          </div>
        </div>

        {credentials?.tempPassword && (
          <div
            className="d-flex gap-2 align-items-start mt-3"
            style={{ background: palette.amberSoft, color: palette.amber, border: "1px solid #f5d88b", borderRadius: 12, padding: "12px 14px", fontSize: "0.84rem" }}
          >
            <MdWarningAmber size={19} style={{ flex: "0 0 auto", marginTop: 1 }} />
            <span>This password is shown only now. Ask the rider to replace it after their first sign-in.</span>
          </div>
        )}
      </Modal.Body>

      <Modal.Footer style={{ borderTop: `1px solid ${palette.border}`, padding: "16px 26px", justifyContent: "space-between" }}>
        <Button variant="outline-secondary" onClick={copyCredentials} style={{ borderRadius: 10, fontWeight: 700 }}>
          {copied ? <MdCheck className="me-2" /> : <MdContentCopy className="me-2" />}
          {copied ? "Copied" : "Copy sign-in details"}
        </Button>
        <Button onClick={onHide} style={{ background: palette.green, borderColor: palette.green, borderRadius: 10, fontWeight: 700, paddingInline: 24 }}>
          Done
        </Button>
      </Modal.Footer>
    </Modal>
  );
};

export default RiderCredentialsModal;
